import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { useMediaQuery } from "@uidotdev/usehooks";
import {
	AlertCircle,
	Check,
	ChevronsUpDown,
	CreditCard,
	Dot,
	Eye,
	EyeClosed,
	LoaderIcon,
	MapPinHouse,
	Plus,
} from "lucide-react";
import {
	type ComponentProps,
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	type UseFieldArrayReturn,
	useFieldArray,
	useForm,
	useFormContext,
	useWatch,
} from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import HereMap, { type HereMaphandler } from "@/components/shared/here-map";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Input as InputExtended } from "@/components/ui/extended/input";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	MultiSelector,
	MultiSelectorContent,
	MultiSelectorInput,
	MultiSelectorItem,
	MultiSelectorList,
	MultiSelectorTrigger,
} from "@/components/ui/multi-select";
import { PhoneInput } from "@/components/ui/phone-input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { TagsInput } from "@/components/ui/tags-input";
import { Textarea } from "@/components/ui/textarea";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { RELIGIONS } from "@/lib/constants";
import { groupLocationsByCountry } from "@/lib/locations";
import {
	hiddenCopyIds,
	pendingAddressChanges,
	type Timeline,
} from "@/lib/therapist-relocation";
import {
	getFormSchema,
	isStandardRegistrationNumber,
	nikPayload,
	registrationNumberDigits,
	type TherapistFormSchema,
} from "@/lib/therapists";
import { useTherapistEnumLabel } from "@/lib/therapists/enum-label";
import { prunedValues } from "@/lib/therapists/prune-values";
import { cn, goBackHandler } from "@/lib/utils";
import type { Location } from "@/types/admin-portal/location";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import type { Service } from "@/types/admin-portal/service";
import type {
	RegistrationNumberSuggestion,
	Therapist,
	TherapistAddress,
	TherapistEmploymentStatus,
	TherapistEmploymentType,
	TherapistGender,
	TherapistServiceEnums,
} from "@/types/admin-portal/therapist";
import type { GlobalPageProps } from "@/types/globals";
import type { FormMode } from "../../../pages/AdminPortal/Therapist/Upsert";
import { AddressSection } from "./relocation/address-section";
import type { UpcomingVisit } from "./relocation/address-timeline";

// Radix Select has no empty value, so "not filled in" needs a sentinel of its own
const RELIGION_UNSET = "UNSET";

// * for section container'
interface FormSectionContainerProps extends ComponentProps<"div"> {
	number: string | number;
	title: string;
}

function FormSectionContainer({
	number,
	title,
	className,
	children,
}: FormSectionContainerProps) {
	return (
		<div
			className={cn("grid w-full gap-4 grid-cols-1 lg:grid-cols-2", className)}
		>
			<div className="flex items-center space-x-1.5 font-semibold col-span-full">
				<div className="flex items-center justify-center border rounded-full size-5 bg-primary text-primary-foreground">
					<span className="text-xs leading-none">{number}</span>
				</div>
				<h2 className="tracking-tight">{title}</h2>
			</div>

			{children}
		</div>
	);
}

// * for account information form
interface AccountInformationFormProps {
	mode: FormMode;
}

function AccountInformationForm({ mode }: AccountInformationFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const [passwordVisibility, setPasswordVisibility] = useState({
		new: false,
		confirmation: false,
	});

	return (
		<>
			<FormField
				control={form.control}
				name="user.email"
				render={({ field }) => (
					<FormItem className="col-span-1">
						<FormLabel>{t("form.account.email")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								autoFocus={mode === "create"}
								type="email"
								autoComplete="email"
								readOnly={mode === "update"}
								placeholder={t("form.account.email_placeholder")}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			{mode === "create" && (
				<>
					<FormField
						control={form.control}
						name="user.password"
						render={({ field }) => (
							<FormItem className="col-start-1">
								<FormLabel>{t("form.account.password")}</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											type={passwordVisibility.new ? "text" : "password"}
											placeholder={t("form.account.password_placeholder")}
											autoComplete="new-password"
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute -translate-y-1/2 right-1 top-1/2 h-7 w-7 text-muted-foreground"
											onClick={() => {
												setPasswordVisibility({
													...passwordVisibility,
													new: !passwordVisibility.new,
												});
											}}
										>
											{!passwordVisibility.new ? (
												<Eye className="size-4" />
											) : (
												<EyeClosed className="size-4" />
											)}
											<span className="sr-only">
												{t("form.account.toggle_visibility")}
											</span>
										</Button>
									</div>
								</FormControl>
								<FormDescription>
									{t("form.account.password_hint")}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="user.passwordConfirmation"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("form.account.password_confirmation")}</FormLabel>
								<FormControl>
									<div className="relative">
										<Input
											{...field}
											type={
												passwordVisibility.confirmation ? "text" : "password"
											}
											placeholder={t(
												"form.account.password_confirmation_placeholder",
											)}
											autoComplete="new-password"
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute -translate-y-1/2 right-1 top-1/2 h-7 w-7 text-muted-foreground"
											onClick={() => {
												setPasswordVisibility({
													...passwordVisibility,
													confirmation: !passwordVisibility.confirmation,
												});
											}}
										>
											{!passwordVisibility.confirmation ? (
												<Eye className="size-4" />
											) : (
												<EyeClosed className="size-4" />
											)}
											<span className="sr-only">
												{t("form.account.toggle_visibility")}
											</span>
										</Button>
									</div>
								</FormControl>

								<FormMessage />
							</FormItem>
						)}
					/>
				</>
			)}
		</>
	);
}

// * for bank detail form
interface BankDetailFormProps {
	fieldIndex: number;
	bankDetailsForm: UseFieldArrayReturn<TherapistFormSchema>;
}

function BankDetailForm({ fieldIndex, bankDetailsForm }: BankDetailFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const [deleted, setDeleted] = useState(false);

	return (
		<div
			className={cn(
				"grid w-full gap-4",
				deleted
					? "motion-opacity-out-0"
					: "motion-translate-motion-scale-in-0 motion-opacity-in-0 motion-delay-100",
			)}
		>
			<div className="flex items-center justify-between w-full col-span-full">
				<div className="flex flex-row items-center space-x-3">
					<span className="text-sm font-bold">
						{t("form.bank.item", { number: Number(fieldIndex) + 1 })}
					</span>

					<Separator
						orientation="vertical"
						className="h-4 bg-muted-foreground/25"
					/>

					<FormField
						control={form.control}
						name={`bankDetails.${fieldIndex}.active`}
						render={({ field }) => (
							<FormItem className="flex flex-row items-center space-x-2 space-y-0">
								<FormControl>
									<Checkbox
										checked={field.value}
										onCheckedChange={(checked) => {
											if (!checked) return;

											// if enabling bank details, will automatically disable other
											bankDetailsForm.fields.map((field, index) => {
												if (fieldIndex === index) return;
												bankDetailsForm.update(index, {
													...field,
													active: false,
												});
											});
											field.onChange(checked);
										}}
									/>
								</FormControl>
								<div className="space-y-1 leading-none">
									<FormLabel className="text-xs">
										{t("form.bank.set_active")}
									</FormLabel>
								</div>
							</FormItem>
						)}
					/>
				</div>

				<Button
					type="button"
					size="xs"
					variant="destructive"
					onClick={(event) => {
						event.preventDefault();
						if (window.confirm(t("form.delete_confirm"))) {
							setDeleted(true);
							setTimeout(() => {
								bankDetailsForm?.remove(fieldIndex);
							}, 250);
						}
					}}
				>
					{t("form.delete")}
				</Button>
			</div>

			<FormField
				control={form.control}
				name={`bankDetails.${fieldIndex}.bankName`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.bank.name")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="text"
								placeholder={t("form.bank.name_placeholder")}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`bankDetails.${fieldIndex}.accountNumber`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.bank.account_number")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="number"
								min={0}
								placeholder={t("form.bank.account_number_placeholder")}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`bankDetails.${fieldIndex}.accountHolderName`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.bank.holder")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="text"
								placeholder={t("form.bank.holder_placeholder")}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			{bankDetailsForm?.fields?.length > 1 &&
				bankDetailsForm?.fields?.length !== fieldIndex + 1 && (
					<Separator className="w-full mt-4 col-span-full lg:hidden" />
				)}
		</div>
	);
}

// * for contract detail form
// Modalities and specializations are picked from the list their service allows. A service
// without a list falls back to free text, so an unlisted service is never a dead end.
interface EnumOrTagsFieldProps {
	name: "modalities" | "specializations";
	label: string;
	placeholder: string;
	options: string[];
	// the switch the prune keys off: `options` is rebuilt by a memo, so its identity changes
	// on unrelated re-renders and cannot stand in for "the admin picked another profession"
	selectedCode: string | null | undefined;
	// which half of the enums.* catalogue labels this field
	group: "modality" | "specialization";
}

function EnumOrTagsField({
	name,
	label,
	placeholder,
	options,
	selectedCode,
	group,
}: EnumOrTagsFieldProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const enumLabel = useTherapistEnumLabel();
	// undefined until the first run, which is what keeps a legacy therapist's free text from
	// being pruned on mount. See prunedValues for both skip rules.
	const previousCode = useRef<string | null | undefined>(undefined);

	// Drop picks the newly selected service does not offer -- switching a therapist from
	// Fisioterapis to Perawat must not leave "TENS" behind, which the model would reject,
	// nor "MUSCULOSKELETAL", which it would silently accept because Perawat has no list.
	useEffect(() => {
		const previous = previousCode.current;
		previousCode.current = selectedCode;

		const kept = prunedValues(
			previous,
			selectedCode,
			options,
			form.getValues(name) ?? [],
		);
		if (!kept) return;

		form.setValue(name, kept as TherapistFormSchema[typeof name], {
			shouldDirty: true,
			// a prune can empty a required field; without this the admin only finds out at submit
			shouldValidate: true,
		});
	}, [selectedCode, options, name, form]);

	return (
		<FormField
			control={form.control}
			name={name}
			render={({ field }) => (
				<FormItem className="col-span-full">
					<FormLabel>{label}</FormLabel>
					<FormControl>
						{!selectedCode ? (
							<Input disabled placeholder={t("form.contract.service_first")} />
						) : options.length ? (
							<MultiSelector
								values={field.value ?? []}
								onValuesChange={field.onChange}
								// the chips are the only record of what was picked once the list
								// closes, and a raw key is not something an admin can check
								labelFor={(value) => enumLabel(group, value)}
								// Command defaults to h-full, and this FormItem is a grid item with a
								// definite row height -- the picker would eat the whole row and push
								// the label and description out of the box, over the next section
								className="h-auto"
							>
								<MultiSelectorTrigger>
									<MultiSelectorInput placeholder={placeholder} />
								</MultiSelectorTrigger>
								<MultiSelectorContent>
									<MultiSelectorList>
										{options.map((option) => (
											<MultiSelectorItem key={option} value={option}>
												{enumLabel(group, option)}
											</MultiSelectorItem>
										))}
									</MultiSelectorList>
								</MultiSelectorContent>
							</MultiSelector>
						) : (
							<TagsInput
								value={field.value}
								onValueChange={field.onChange}
								placeholder={placeholder}
							/>
						)}
					</FormControl>

					<FormDescription>
						{!selectedCode ? (
							t("form.contract.service_first")
						) : options.length ? (
							t("form.contract.enum_hint")
						) : (
							<Trans
								i18nKey="form.contract.tags_hint"
								ns="therapists"
								components={[<b key="key" />]}
							/>
						)}
					</FormDescription>

					<FormMessage />
				</FormItem>
			)}
		/>
	);
}

// * for registration number form
// Nearly every stored number is "<service code>-<digits>", so the prefix is shown as a fixed
// affix and the admin types digits only — fewer characters to mistype. The affix follows the
// selected service. Numbers that do not fit the shape (the operations spreadsheet does not
// promise it) are entered whole under "custom", since ADR 0003 forbids rejecting them.
interface RegistrationNumberFormProps {
	serviceCode?: string;
	suggestions: string[];
}

function RegistrationNumberForm({
	serviceCode,
	suggestions,
}: RegistrationNumberFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const prefix = serviceCode ? `${serviceCode}-` : "";
	// a stored number that does not fit the shape opens in custom mode, so editing an unusual
	// number never silently reshapes it into one the affix can express
	const customToggleId = useId();
	const [isCustom, setIsCustom] = useState(() => {
		const current = form.getValues("registrationNumber");
		return !!current && !isStandardRegistrationNumber(current);
	});

	// Re-apply the prefix when the selected service changes, so the stored value matches the
	// affix the admin is looking at. It runs on load too, which is deliberate: a therapist
	// whose number predates a service change sees the new prefix before saving rather than
	// discovering it afterwards.
	useEffect(() => {
		if (isCustom || !prefix) return;

		const current = form.getValues("registrationNumber") ?? "";
		const digits = registrationNumberDigits(current);
		if (digits === undefined) return;

		const next = `${prefix}${digits}`;
		if (next !== current) {
			form.setValue("registrationNumber", next, { shouldDirty: true });
		}
	}, [prefix, isCustom, form]);

	return (
		<FormField
			control={form.control}
			name="registrationNumber"
			render={({ field }) => {
				const digits = registrationNumberDigits(field.value ?? "");

				return (
					<FormItem className="col-start-1">
						<FormLabel>{t("form.registration.label")}</FormLabel>
						<FormControl>
							{isCustom ? (
								<Input
									{...field}
									type="text"
									autoComplete="off"
									placeholder={t("form.registration.custom_placeholder")}
								/>
							) : (
								<div className="flex rounded-md shadow-sm">
									<span className="inline-flex items-center px-3 text-sm border border-r-0 rounded-l-md bg-muted text-muted-foreground">
										{prefix || "—"}
									</span>
									<Input
										name={field.name}
										ref={field.ref}
										onBlur={field.onBlur}
										// text, not number: the digits are zero-padded ("0372"), and a
										// number input would drop the padding the format depends on
										type="text"
										inputMode="numeric"
										autoComplete="off"
										placeholder={t("form.registration.digits_placeholder")}
										className="rounded-l-none"
										value={digits ?? ""}
										onChange={(event) =>
											field.onChange(`${prefix}${event.target.value}`)
										}
									/>
								</div>
							)}
						</FormControl>

						<FormDescription>
							{suggestions.length
								? t("form.registration.hint_suggested", {
										suggestions: suggestions.join(", "),
									})
								: t("form.registration.hint")}
						</FormDescription>

						<div className="flex items-center gap-2">
							<Checkbox
								id={customToggleId}
								checked={isCustom}
								onCheckedChange={(checked) => setIsCustom(checked === true)}
							/>
							{/* htmlFor rather than wrapping: Radix renders the checkbox as a
							    button, which a wrapping label does not reliably toggle */}
							<label
								htmlFor={customToggleId}
								className="text-sm cursor-pointer text-muted-foreground"
							>
								{t("form.registration.custom_toggle")}
							</label>
						</div>

						<FormMessage />
					</FormItem>
				);
			}}
		/>
	);
}

interface ContractDetailFormProps {
	currentPath: string;
	services: Service[];
	employmentTypes: TherapistEmploymentType;
	employmentStatuses: TherapistEmploymentStatus;
	registrationNumberSuggestions: RegistrationNumberSuggestion[];
	therapistEnums: TherapistServiceEnums[];
}

function ContractDetailForm({
	currentPath,
	services,
	employmentTypes,
	employmentStatuses,
	registrationNumberSuggestions,
	therapistEnums,
}: ContractDetailFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const [isLoading, setIsLoading] = useState({
		services: false,
		employmentStatuses: false,
	});

	// the suggestion follows the selected service, since that is the prefix a tidily numbered
	// therapist would get. On a fresh create form no service is picked yet, so every prefix
	// is offered rather than leaving the description empty on the one screen that most wants
	// a next number. It stays a hint either way: the number is typed by hand.
	// useWatch, not form.watch: watch() off the context subscribes the component that owns
	// useForm, so picking a service re-rendered the Service field but left this one stale
	const selectedServiceCode = useWatch({
		control: form.control,
		name: "service.code",
	});
	const suggestedRegistrationNumbers = useMemo(() => {
		const suggestions = registrationNumberSuggestions ?? [];
		const forSelectedService = suggestions.filter(
			(item) => item.prefix === selectedServiceCode,
		);

		return (selectedServiceCode ? forSelectedService : suggestions).map(
			(item) => item.suggestion,
		);
	}, [registrationNumberSuggestions, selectedServiceCode]);

	// modalities and specializations are closed lists per profession, keyed by the service code.
	// A service with no list (Perawat has no specializations, and an unlisted service has
	// neither) keeps the old free-text input rather than showing an empty picker.
	const enumsForService = useMemo(() => {
		const match = therapistEnums?.find(
			(item) => item.code === selectedServiceCode,
		);

		return {
			modalities: match?.modalities ?? [],
			specializations: match?.specializations ?? [],
		};
	}, [therapistEnums, selectedServiceCode]);

	// the list items for select options
	const [selectCollections, setSelectCollections] = useState(() => {
		const employmentStatusList =
			employmentStatuses?.map((status) => {
				return {
					value: status.toUpperCase(),
					label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
					color:
						status === "ACTIVE"
							? "text-emerald-600"
							: status === "INACTIVE"
								? "text-destructive"
								: "text-yellow-600",
				};
			}) || [];

		return {
			employmentStatuses: employmentStatusList as {
				value: string;
				label: string;
				color: string;
			}[],
		};
	});
	const fetchData = {
		getEmploymentStatuses: () => {
			router.get(
				currentPath,
				{},
				{
					only: ["employmentStatuses"],
					preserveState: true,
					preserveScroll: true,
					onStart: () => {
						setIsLoading((prev) => ({
							...prev,
							employmentStatuses: true,
						}));
					},
					onSuccess: () => {
						const employmentStatusList = employmentStatuses.map((status) => {
							return {
								value: status.toUpperCase(),
								label:
									status.charAt(0).toUpperCase() +
									status.slice(1).toLowerCase(),
								color:
									status === "ACTIVE"
										? "text-emerald-600"
										: status === "INACTIVE"
											? "text-destructive"
											: "text-yellow-600",
							};
						});

						setSelectCollections((prev) => ({
							...prev,
							employmentStatuses: employmentStatusList,
						}));
					},
					onFinish: () => {
						setTimeout(() => {
							setIsLoading((prev) => ({
								...prev,
								employmentStatuses: false,
							}));
						}, 250);
					},
				},
			);
		},
		getServices: () => {
			router.get(
				currentPath,
				{},
				{
					only: ["services"],
					preserveState: true,
					preserveScroll: true,
					onStart: () => {
						setIsLoading((prev) => ({
							...prev,
							services: true,
						}));
					},
					onFinish: () => {
						setTimeout(() => {
							setIsLoading((prev) => ({
								...prev,
								services: false,
							}));
						}, 250);
					},
				},
			);
		},
	};

	// for bank details array of fields
	const bankDetailsForm = useFieldArray({
		control: form.control,
		name: "bankDetails",
		rules: {
			required: true,
			minLength: 1,
		},
	});

	return (
		<>
			<FormField
				control={form.control}
				name="batch"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.contract.batch")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="number"
								min={0}
								value={field?.value || ""}
								placeholder={t("form.contract.batch_placeholder")}
								className="w-[35%] md:w-[25%] lg:w-[35%] xl:w-[35%]"
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="service.name"
				render={({ field }) => (
					<FormItem className="grid !mt-1 self-end">
						<FormLabel className="h-6">{t("form.contract.service")}</FormLabel>
						<Popover>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										className={cn(
											"w-[75%] md:w-[50%] lg:w-[75%] xl:w-[75%] justify-between text-muted-foreground font-normal !mt-0 px-3",
											!field.value && "text-muted-foreground",
										)}
										onClick={() => {
											fetchData.getServices();
										}}
									>
										{field.value
											? services
													?.find((service) => service.name === field.value)
													?.name.replaceAll("_", " ") || field.value
											: t("form.contract.service_select")}
										<ChevronsUpDown className="opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent align="start" className="w-[250px] p-0">
								<Command>
									<CommandInput
										placeholder={t("form.contract.service_search")}
									/>
									<CommandList>
										<CommandEmpty>
											{t("form.contract.service_empty")}
										</CommandEmpty>
										<CommandGroup>
											{isLoading.services ? (
												<CommandItem value={undefined} disabled>
													<LoaderIcon className="animate-spin" />
													<span>{t("form.wait")}</span>
												</CommandItem>
											) : (
												services?.map((service) => (
													<CommandItem
														value={String(service.id)}
														key={service.id}
														onSelect={() => {
															const { id, name, code } = service;
															form.setValue("service", {
																id,
																name,
																code,
															});
														}}
													>
														<Check
															className={cn(
																"mr-2 h-4 w-4",
																service.name === field.value
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
														{service.name.replaceAll("_", " ")}
													</CommandItem>
												))
											)}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>

						<FormMessage />
					</FormItem>
				)}
			/>

			<RegistrationNumberForm
				serviceCode={selectedServiceCode}
				suggestions={suggestedRegistrationNumbers}
			/>

			<FormField
				control={form.control}
				name="employmentType"
				render={({ field }) => (
					<FormItem className="col-start-1">
						<FormLabel>{t("form.contract.employment_type")}</FormLabel>
						<Select
							onValueChange={field.onChange}
							defaultValue={field?.value || ""}
						>
							<FormControl>
								<SelectTrigger
									className={`w-[50%] md:w-[35%] lg:w-[50%] xl:w-[50%] ${!field?.value ? "text-muted-foreground" : ""}`}
								>
									<SelectValue
										placeholder={t("form.contract.employment_type_placeholder")}
									/>
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{!!employmentTypes?.length &&
									employmentTypes.map((type) => (
										<SelectItem key={type} value={type}>
											{type}
										</SelectItem>
									))}
							</SelectContent>
						</Select>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="employmentStatus"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.contract.employment_status")}</FormLabel>
						<Select
							onValueChange={field.onChange}
							defaultValue={field?.value || ""}
							onOpenChange={(value) => {
								if (value) {
									fetchData.getEmploymentStatuses();
								}
							}}
						>
							<FormControl>
								<SelectTrigger
									className={`w-[50%] md:w-[35%] lg:w-[50%] xl:w-[50%] ${!field?.value ? "text-muted-foreground" : ""}`}
								>
									<SelectValue
										placeholder={t(
											"form.contract.employment_status_placeholder",
										)}
									/>
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{isLoading.employmentStatuses ? (
									<SelectItem value="loading" disabled>
										<LoaderIcon className="animate-spin" />
										<span>{t("form.wait")}</span>
									</SelectItem>
								) : selectCollections?.employmentStatuses?.length ? (
									selectCollections?.employmentStatuses.map((status) => (
										<SelectItem key={status.value} value={status.value}>
											<span className="flex items-center gap-2">
												<Dot
													className={status.color}
													width={10}
													height={10}
													strokeWidth={20}
												/>
												<span className="truncate">{status.label}</span>
											</span>
										</SelectItem>
									))
								) : (
									<SelectItem value="no items" disabled>
										{t("form.contract.employment_status_empty")}
									</SelectItem>
								)}
							</SelectContent>
						</Select>
						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="contractStartDate"
				render={({ field }) => (
					<FormItem>
						<FormLabel className="capitalize">
							{t("form.contract.start_date")}{" "}
							<span className="text-sm italic font-light">
								{t("form.optional")}
							</span>
						</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="date"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								className="w-[65%] md:w-[50%] lg:w-[65%] xl:w-[65%] block"
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="contractEndDate"
				render={({ field }) => (
					<FormItem>
						<FormLabel className="capitalize">
							{t("form.contract.end_date")}{" "}
							<span className="text-sm italic font-light">
								{t("form.optional")}
							</span>
						</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="date"
								value={field.value || ""}
								onChange={(e) => field.onChange(e.target.value || null)}
								className="w-[65%] md:w-[50%] lg:w-[65%] xl:w-[65%] block"
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>

			<EnumOrTagsField
				name="modalities"
				label={t("form.contract.modality")}
				placeholder={t("form.contract.modality_placeholder")}
				options={enumsForService.modalities}
				selectedCode={selectedServiceCode}
				group="modality"
			/>

			<EnumOrTagsField
				name="specializations"
				label={t("form.contract.specialization")}
				placeholder={t("form.contract.specialization_placeholder")}
				options={enumsForService.specializations}
				selectedCode={selectedServiceCode}
				group="specialization"
			/>

			<div className="grid grid-cols-1 gap-6 mt-4 col-span-full lg:grid-cols-2">
				<div className="grid gap-4 col-span-full">
					<div className="flex items-center gap-2">
						<CreditCard className="size-4" />
						<p className="text-sm font-semibold tracking-tight">
							{t("form.bank.heading")}
						</p>
					</div>

					<Separator />
				</div>

				{bankDetailsForm?.fields?.length ? (
					bankDetailsForm?.fields?.map((bankDetail, fieldIndex) => {
						return (
							<BankDetailForm
								key={bankDetail.id}
								fieldIndex={fieldIndex}
								bankDetailsForm={bankDetailsForm}
							/>
						);
					})
				) : (
					<p className="w-10/12 mx-auto text-sm font-light text-center col-span-full lg:w-5/12">
						{t("form.bank.empty")}
					</p>
				)}

				<Button
					disabled={
						!!form.formState.errors?.bankDetails &&
						!!bankDetailsForm.fields?.length
					}
					size="sm"
					type="button"
					variant="primary-outline"
					className="w-full mx-auto col-span-full lg:w-4/12"
					onClick={() => {
						bankDetailsForm?.append({
							bankName: "",
							accountNumber: "",
							accountHolderName: "",
							active: false,
						});
						form.trigger("bankDetails");
					}}
				>
					<Plus className="mr-0.5" />
					{t("form.bank.add")}
				</Button>
			</div>
		</>
	);
}

// * for address form
interface AddressFormProps {
	currentPath: string;
	fieldIndex: number;
	locations: Location[];
	addressesForm?: UseFieldArrayReturn<TherapistFormSchema>;
	canSetActive: boolean;
	/** Only the fields: the edit form's Rak owns the row's title, delete, and Jadikan Utama. */
	bare?: boolean;
}

function AddressForm({
	currentPath,
	fieldIndex,
	locations,
	addressesForm,
	canSetActive,
	bare = false,
}: AddressFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const [isLoading, setIsLoading] = useState({
		states: false,
		cities: false,
	});
	const [deleted, setDeleted] = useState(false);
	const [latLngInputMethod, setLatLngInputMethod] = useState<
		"manual" | "automatic"
	>("manual");
	const [isGeocoding, setIsGeocoding] = useState(false);
	const [geocodingError, setGeocodingError] = useState<string | null>(null);

	// the list items for select options
	const [selectCollections, setSelectCollections] = useState(() => {
		return {
			states: [] as string[],
			cities: [] as string[],
		};
	});
	const fetchData = {
		getLocations: (query: {
			country?: string | null;
			state?: string | null;
			city?: string | null;
		}) => {
			router.get(currentPath, query, {
				only: ["locations"],
				preserveState: true,
				preserveScroll: true,
				onStart: () => {
					setIsLoading((prev) => ({
						...prev,
						states: !!query?.country,
						cities: !!query?.state,
					}));
				},
				onSuccess: () => {
					// setup the states list
					if (query?.country) {
						const states =
							groupLocationsByCountry(locations)
								?.filter((location) => location.country === query.country)
								?.flatMap((location) =>
									location.states.map((state) => state.name),
								) || [];
						setSelectCollections((prev) => ({ ...prev, states }));
					}

					// setup the cities list
					if (query?.state) {
						const cities =
							locations
								?.filter((location) => location.state === query.state)
								?.map((location) => location.city) || [];
						setSelectCollections((prev) => ({ ...prev, cities }));
					}
				},
				onFinish: () => {
					setTimeout(() => {
						setIsLoading((prev) => ({
							...prev,
							states: false,
							cities: false,
						}));
					}, 250);
				},
			});
		},
	};

	// for addresses array of fields
	const watchAddresses = useWatch({ name: "addresses" });
	const address = useMemo<TherapistFormSchema["addresses"][number]>(
		() => watchAddresses?.[fieldIndex],
		[watchAddresses?.[fieldIndex], fieldIndex],
	);
	const coordinate = useMemo(() => {
		const coords = [address.lat, address.lng].filter(
			(coord): coord is number => coord != null && !Number.isNaN(coord),
		);
		// Return empty array if no valid coordinates, HereMap will handle it
		return coords.length === 2 ? coords : [];
	}, [address.lat, address.lng]);

	// Get coordinate string from coordinates field
	const getCoordinateString = useCallback(() => {
		const coordinates = form.getValues(`addresses.${fieldIndex}.coordinates`);
		return coordinates || "";
	}, [form, fieldIndex]);

	// Populate coordinates field from existing lat/lng data on form load
	useEffect(() => {
		const lat = address?.lat;
		const lng = address?.lng;
		const currentCoordinates = address?.coordinates;

		// If we have lat/lng but no coordinates field, populate it
		if (
			lat != null &&
			lng != null &&
			!Number.isNaN(lat) &&
			!Number.isNaN(lng) &&
			(!currentCoordinates || currentCoordinates.trim() === "")
		) {
			const coordString = `${lat}, ${lng}`;
			form.setValue(`addresses.${fieldIndex}.coordinates`, coordString, {
				shouldValidate: false,
			});
		}
	}, [address?.lat, address?.lng, address?.coordinates, fieldIndex, form]);

	// Add marker to map when coordinates are available (following patient form pattern)
	useEffect(() => {
		const coordinates = address?.coordinates;

		if (coordinates && mapRef.current?.marker?.onAdd) {
			const [lat, lng] = coordinates
				.split(",")
				.map((s) => parseFloat(s.trim()));
			if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
				// Get current form values to avoid stale closures
				const currentAddress = form.getValues(
					`addresses.${fieldIndex}.address`,
				);
				const fullAddress =
					currentAddress || t("form.coordinate.custom_location");

				// Add marker at the coordinate position
				mapRef.current.marker.onAdd(
					[
						{
							position: { lat, lng },
							address: fullAddress,
						},
					],
					{ changeMapView: true },
				);

				// Update lat/lng fields to match coordinates
				form.setValue(`addresses.${fieldIndex}.lat`, lat);
				form.setValue(`addresses.${fieldIndex}.lng`, lng);

				// Cleanup function to remove marker when coordinates change
				return () => {
					try {
						if (mapRef.current?.marker?.onRemove) {
							mapRef.current.marker.onRemove();
						}
					} catch (error) {
						// Ignore errors if marker is already removed
						console.warn("Marker cleanup error:", error);
					}
				};
			}
		} else {
			// Clear lat/lng when coordinates are empty
			form.setValue(`addresses.${fieldIndex}.lat`, null);
			form.setValue(`addresses.${fieldIndex}.lng`, null);
		}
	}, [address?.coordinates, fieldIndex, form, t]);

	// for map state management
	const mapRef = useRef<(H.Map & HereMaphandler) | null>(null);
	// ===== COORDINATE HANDLERS =====
	const handleResetCoordinates = useCallback(() => {
		form.setValue(`addresses.${fieldIndex}.lat`, null);
		form.setValue(`addresses.${fieldIndex}.lng`, null);
		form.setValue(`addresses.${fieldIndex}.coordinates`, "");
		setGeocodingError(null);

		// remove the map markers
		try {
			mapRef.current?.marker?.onRemove?.();
		} catch (error) {
			// Ignore errors if marker is already removed
			console.warn("Marker reset error:", error);
		}
	}, [form, fieldIndex]);

	// Handle view on Google Maps
	const handleViewOnGoogleMaps = useCallback(() => {
		const coordStr = getCoordinateString();
		if (coordStr) {
			window.open(
				`https://www.google.com/maps/search/?api=1&query=${coordStr}`,
				"_blank",
			);
		}
	}, [getCoordinateString]);

	// ===== GEOCODING =====
	const handleGeocoding = useCallback(async () => {
		const address = form.getValues(`addresses.${fieldIndex}.address`);
		const city = form.getValues(`addresses.${fieldIndex}.city`);
		const state = form.getValues(`addresses.${fieldIndex}.state`);
		const country = form.getValues(`addresses.${fieldIndex}.country`);

		// Clear any previous errors
		setGeocodingError(null);

		if (!address || address.trim() === "") {
			console.error("Address is required for geocoding");
			toast.error(t("form.coordinate.no_address"));
			setGeocodingError(t("form.coordinate.no_address"));
			return;
		}

		if (!city || !state || !country) {
			console.error("Location details are required for geocoding");
			toast.error(t("form.coordinate.no_location"));
			setGeocodingError(t("form.coordinate.no_location"));
			return;
		}

		setIsGeocoding(true);
		try {
			const fullAddress = `${address}, ${city}, ${state}, ${country}`;

			// Use Here Maps geocoding via the map component
			if (mapRef.current?.geocode?.onCalculate) {
				const result = await mapRef.current.geocode.onCalculate();

				if (result?.position) {
					const { lat, lng } = result.position;
					const coordString = `${lat}, ${lng}`;
					form.setValue(`addresses.${fieldIndex}.lat`, lat);
					form.setValue(`addresses.${fieldIndex}.lng`, lng);
					form.setValue(`addresses.${fieldIndex}.coordinates`, coordString);

					// Add marker to map at the calculated position
					if (mapRef.current?.marker?.onAdd) {
						mapRef.current.marker.onAdd(
							[
								{
									position: { lat, lng },
									address: fullAddress,
								},
							],
							{ changeMapView: true },
						);
					}

					// Clear any errors on success
					setGeocodingError(null);

					// Show success toast
					toast.success(t("form.coordinate.success"));
				} else {
					throw new Error(t("form.coordinate.no_results"));
				}
			} else {
				throw new Error(t("form.coordinate.unavailable"));
			}
		} catch (error) {
			console.error("Geocoding error:", error);
			const errorMessage =
				error instanceof Error ? error.message : t("form.coordinate.failed");
			toast.error(errorMessage);
			setGeocodingError(errorMessage);
		} finally {
			setIsGeocoding(false);
		}
	}, [form, fieldIndex, t]);

	return (
		<div
			className={cn(
				"grid w-full gap-4 lg:grid-cols-2",
				deleted
					? "motion-opacity-out-0"
					: "motion-translate-motion-scale-in-0 motion-opacity-in-0 motion-delay-100",
			)}
		>
			{!bare && addressesForm && (
				<div className="flex items-center justify-between w-full col-span-full">
					<div className="flex flex-row items-center space-x-3">
						<span className="text-sm font-bold">
							{t("form.address.item", { number: Number(fieldIndex) + 1 })}
						</span>

						{canSetActive && (
							<>
								<Separator
									orientation="vertical"
									className="h-4 bg-muted-foreground/25"
								/>

								<FormField
									control={form.control}
									name={`addresses.${fieldIndex}.active`}
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-2 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={(checked) => {
														if (!checked) return;

														addressesForm.fields.map((field, index) => {
															if (fieldIndex === index) return;
															addressesForm.update(index, {
																...field,
																active: false,
															});
														});
														field.onChange(checked);
													}}
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel className="text-xs">
													{t("form.address.set_active")}
												</FormLabel>
											</div>
										</FormItem>
									)}
								/>
							</>
						)}
					</div>

					<Button
						type="button"
						size="xs"
						variant="destructive"
						onClick={(event) => {
							event.preventDefault();
							if (window.confirm(t("form.delete_confirm"))) {
								setDeleted(true);
								setTimeout(() => {
									addressesForm.remove(fieldIndex);
								}, 250);
							}
						}}
					>
						{t("form.delete")}
					</Button>
				</div>
			)}

			<FormField
				control={form.control}
				name={`addresses.${fieldIndex}.country`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.address.country")}</FormLabel>
						<FormControl>
							<InputExtended
								{...field}
								readOnly
								type="text"
								placeholder={t("form.address.country_placeholder")}
								autoComplete="country-name"
							>
								<InputExtended.Group>
									<InputExtended.RightIcon>
										<span>{watchAddresses?.[fieldIndex]?.countryCode}</span>
									</InputExtended.RightIcon>
								</InputExtended.Group>
							</InputExtended>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`addresses.${fieldIndex}.state`}
				render={({ field }) => (
					<FormItem className="grid !mt-1 self-end">
						<FormLabel className="h-6">{t("form.address.state")}</FormLabel>
						<Popover modal>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										className={cn(
											"w-[75%] md:w-[50%] lg:w-[75%] xl:w-[75%] justify-between text-muted-foreground font-normal !mt-0 px-3",
											!field.value && "text-muted-foreground",
										)}
										onClick={() => {
											fetchData.getLocations({
												country: watchAddresses?.[fieldIndex]?.country,
											});
										}}
									>
										<span className="truncate">
											{field.value
												? selectCollections?.states?.find(
														(state) => state === field.value,
													) || field.value
												: t("form.address.state_select")}
										</span>
										<ChevronsUpDown className="opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent align="start" className="w-[250px] p-0">
								<Command>
									<CommandInput
										disabled={isLoading.states}
										placeholder={t("form.address.state_search")}
										autoComplete="address-level1"
									/>
									<CommandList>
										<CommandEmpty>{t("form.address.state_empty")}</CommandEmpty>

										<CommandGroup>
											{isLoading.states ? (
												<CommandItem value={undefined} disabled>
													<LoaderIcon className="animate-spin" />
													<span>{t("form.wait")}</span>
												</CommandItem>
											) : (
												selectCollections?.states?.map((state) => (
													<CommandItem
														value={state}
														key={state}
														onSelect={async () => {
															// set the state data selected
															form.setValue(
																`addresses.${fieldIndex}.state`,
																state,
															);
															form.trigger(`addresses.${fieldIndex}.state`);
														}}
													>
														{state}
														<Check
															className={cn(
																"ml-auto",
																state === field.value
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
													</CommandItem>
												))
											)}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`addresses.${fieldIndex}.city`}
				render={({ field }) => (
					<FormItem className="grid !mt-1 self-end">
						<FormLabel className="h-6">{t("form.address.city")}</FormLabel>
						<Popover modal>
							<PopoverTrigger asChild>
								<FormControl>
									<Button
										variant="outline"
										className={cn(
											"w-[75%] md:w-[50%] lg:w-[75%] xl:w-[75%] justify-between text-muted-foreground font-normal !mt-0 px-3",
											!field.value && "text-muted-foreground",
										)}
										onClick={() => {
											fetchData.getLocations({
												state: form.getValues(`addresses.${fieldIndex}.state`),
											});
										}}
									>
										<span className="truncate">
											{field.value
												? selectCollections?.cities?.find(
														(city) => city === field.value,
													) || field.value
												: t("form.address.city_select")}
										</span>
										<ChevronsUpDown className="opacity-50" />
									</Button>
								</FormControl>
							</PopoverTrigger>
							<PopoverContent align="start" className="w-[250px] p-0">
								<Command>
									<CommandInput
										disabled={isLoading.cities}
										placeholder={t("form.address.city_search")}
										autoComplete="address-level2"
									/>
									<CommandList>
										<CommandEmpty>{t("form.address.city_empty")}</CommandEmpty>

										<CommandGroup>
											{isLoading.cities ? (
												<CommandItem value={undefined} disabled>
													<LoaderIcon className="animate-spin" />
													<span>{t("form.wait")}</span>
												</CommandItem>
											) : (
												selectCollections?.cities?.map((city) => (
													<CommandItem
														value={city}
														key={city}
														onSelect={async () => {
															// set the city data selected
															form.setValue(
																`addresses.${fieldIndex}.city`,
																city,
															);
															form.trigger(`addresses.${fieldIndex}.city`);
														}}
													>
														{city}
														<Check
															className={cn(
																"ml-auto",
																city === field.value
																	? "opacity-100"
																	: "opacity-0",
															)}
														/>
													</CommandItem>
												))
											)}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`addresses.${fieldIndex}.postalCode`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>
							{t("form.address.postal_code")}{" "}
							<span className="text-sm italic font-light">
								{t("form.optional")}
							</span>
						</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="text"
								placeholder={t("form.address.postal_code_placeholder")}
								autoComplete="postal-code"
								className="w-[50%] md:w-[35%] lg:w-[50%] xl:w-[50%]"
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name={`addresses.${fieldIndex}.address`}
				render={({ field }) => (
					<FormItem className="col-span-full">
						<FormLabel>{t("form.address.address")}</FormLabel>
						<FormControl>
							<Textarea
								placeholder={t("form.address.address_placeholder")}
								autoComplete="street-address"
								// className="resize-none"
								{...field}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<div className="space-y-4 col-span-full">
				<div className="space-y-2">
					<div>
						<span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
							{t("form.coordinate.label")}
						</span>
					</div>

					<div className="flex items-center gap-2">
						<div className="flex items-center space-x-2 min-w-0">
							<Select
								value={latLngInputMethod}
								onValueChange={(value) =>
									setLatLngInputMethod(value as "manual" | "automatic")
								}
							>
								<SelectTrigger className="w-[140px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="manual">
										{t("form.coordinate.manual")}
									</SelectItem>
									<SelectItem value="automatic">
										{t("form.coordinate.automatic")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<FormField
							control={form.control}
							name={`addresses.${fieldIndex}.coordinates`}
							render={({ field }) => (
								<FormItem className="flex-1 min-w-0">
									<FormControl>
										<Input
											{...field}
											value={field.value || ""}
											onChange={(e) => field.onChange(e.target.value.trim())}
											placeholder={t("form.coordinate.placeholder")}
											readOnly={latLngInputMethod === "automatic"}
											{...(latLngInputMethod === "automatic" && {
												tabIndex: -1,
											})}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				{latLngInputMethod === "automatic" && (
					<div className="flex gap-2">
						<Button
							type="button"
							variant="primary-outline"
							onClick={handleGeocoding}
							disabled={
								isGeocoding ||
								!form.getValues(`addresses.${fieldIndex}.address`)
							}
							className="w-full"
						>
							{isGeocoding && (
								<LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
							)}
							{isGeocoding
								? t("form.coordinate.geocoding")
								: t("form.coordinate.geocode")}
						</Button>

						{getCoordinateString() && (
							<>
								<Button
									type="button"
									variant="destructive-outline"
									disabled={isGeocoding}
									onClick={handleResetCoordinates}
									className="flex-shrink-0"
								>
									{t("form.coordinate.reset")}
								</Button>

								<Button
									type="button"
									variant="outline"
									disabled={isGeocoding}
									onClick={handleViewOnGoogleMaps}
									className="flex-shrink-0"
								>
									{t("form.coordinate.view_on_maps")}
								</Button>
							</>
						)}
					</div>
				)}

				{geocodingError && (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertTitle>{t("form.coordinate.error_title")}</AlertTitle>
						<AlertDescription>{geocodingError}</AlertDescription>
					</Alert>
				)}
			</div>

			<HereMap
				ref={mapRef}
				coordinate={coordinate}
				address={{
					country: address.country,
					state: address.state,
					city: address.city,
					postalCode: address?.postalCode || "",
					address: address.address,
				}}
				options={{ disabledEvent: true }}
				className="col-span-full"
			/>

			{!bare &&
				addressesForm &&
				addressesForm.fields.length > 1 &&
				addressesForm.fields.length !== fieldIndex + 1 && (
					<Separator className="w-full mt-4 col-span-full" />
				)}
		</div>
	);
}

// * for personal information form
interface PersonalInformationFormProps {
	currentPath: string;
	genders: TherapistGender;
	locations: Location[];
	nikMasked?: string | null;
	/** False until fisiohome-backend migration 0124 adds the column; the field stays hidden. */
	religionSupported: boolean;
	/** The edit form's Alamat section; the create form keeps the plain list (X10). */
	addressSection?: ReactNode;
}

function PersonalInformationForm({
	currentPath,
	genders,
	locations,
	nikMasked,
	religionSupported,
	addressSection,
}: PersonalInformationFormProps) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");

	return (
		<>
			<FormField
				control={form.control}
				name="name"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.personal.name")}</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="text"
								autoComplete="name"
								placeholder={t("form.personal.name_placeholder")}
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="nik"
				render={({ field }) => {
					const removing = field.value === null;

					return (
						<FormItem>
							<FormLabel>
								{t("form.personal.nik")}{" "}
								<span className="text-sm italic font-light">
									{t("form.personal.nik_optional")}
								</span>
							</FormLabel>
							<div className="flex items-center gap-2">
								<FormControl>
									<Input
										{...field}
										value={field.value ?? ""}
										type="text"
										inputMode="numeric"
										autoComplete="off"
										disabled={removing}
										placeholder={
											nikMasked || t("form.personal.nik_placeholder")
										}
									/>
								</FormControl>
								{nikMasked && (
									<Button
										type="button"
										variant={removing ? "outline" : "destructive-outline"}
										onClick={() =>
											form.setValue("nik", removing ? "" : null, {
												shouldDirty: true,
												shouldValidate: true,
											})
										}
									>
										{removing
											? t("form.personal.nik_remove_undo")
											: t("form.personal.nik_remove")}
									</Button>
								)}
							</div>
							{nikMasked && (
								<FormDescription className={cn(removing && "text-destructive")}>
									{removing
										? t("form.personal.nik_remove_pending")
										: t("form.personal.nik_keep_hint", { masked: nikMasked })}
								</FormDescription>
							)}
							<FormMessage />
						</FormItem>
					);
				}}
			/>

			<FormField
				control={form.control}
				name="phoneNumber"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.personal.phone")}</FormLabel>
						<FormControl>
							<PhoneInput
								{...field}
								placeholder={t("form.personal.phone_placeholder")}
								defaultCountry="ID"
								autoComplete="tel"
								international
							/>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={form.control}
				name="gender"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("form.personal.gender")}</FormLabel>
						<Select
							onValueChange={field.onChange}
							defaultValue={field?.value || ""}
						>
							<FormControl>
								<SelectTrigger
									className={`w-[50%] md:w-[35%] lg:w-[25%] xl:w-[25%] ${!field?.value ? "text-muted-foreground" : ""}`}
								>
									<SelectValue
										placeholder={t("form.personal.gender_placeholder")}
									/>
								</SelectTrigger>
							</FormControl>
							<SelectContent>
								{genders?.length &&
									genders?.map((gender) => (
										<SelectItem key={gender} value={gender}>
											{gender}
										</SelectItem>
									))}
							</SelectContent>
						</Select>

						<FormMessage />
					</FormItem>
				)}
			/>

			{religionSupported && (
				<FormField
					control={form.control}
					name="religion"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								{t("form.personal.religion")}{" "}
								<span className="text-sm italic font-light">
									{t("form.personal.religion_optional")}
								</span>
							</FormLabel>
							<Select
								onValueChange={(value) =>
									field.onChange(value === RELIGION_UNSET ? null : value)
								}
								value={field.value ?? RELIGION_UNSET}
							>
								<FormControl>
									<SelectTrigger
										className={`w-[50%] md:w-[35%] lg:w-[50%] xl:w-[50%] ${!field.value ? "text-muted-foreground" : ""}`}
									>
										<SelectValue />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									<SelectItem value={RELIGION_UNSET}>
										{t("enums.religion_unset")}
									</SelectItem>
									{RELIGIONS.map((religion) => (
										<SelectItem key={religion} value={religion}>
											{t(`enums.religion.${religion}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormDescription>
								{t("form.personal.religion_hint")}
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>
			)}

			<FormField
				control={form.control}
				name="telegramId"
				render={({ field }) => (
					<FormItem>
						<FormLabel className="capitalize">
							{t("form.personal.telegram")}{" "}
							<span className="text-sm italic font-light">
								{t("form.personal.telegram_optional")}
							</span>
						</FormLabel>
						<FormControl>
							<Input
								{...field}
								type="text"
								placeholder={t("form.personal.telegram_placeholder")}
								autoComplete="off"
							/>
						</FormControl>
						<FormDescription>
							{t("form.personal.telegram_hint")}
						</FormDescription>
						<FormMessage />
					</FormItem>
				)}
			/>

			<div className="grid grid-cols-1 gap-6 mt-4 col-span-full">
				<div className="grid gap-4">
					<div className="flex items-center gap-2">
						<MapPinHouse className="size-4" />
						<p className="text-sm font-semibold tracking-tight">
							{t("form.address.heading")}
						</p>
					</div>

					<Separator />
				</div>

				{addressSection ?? (
					<AddressList currentPath={currentPath} locations={locations} />
				)}
			</div>
		</>
	);
}

// * the create form's address list: every address editable, one marked active
function AddressList({
	currentPath,
	locations,
}: {
	currentPath: string;
	locations: Location[];
}) {
	const form = useFormContext<TherapistFormSchema>();
	const { t } = useTranslation("therapists");
	const addressesForm = useFieldArray({
		control: form.control,
		name: "addresses",
		rules: { required: true, minLength: 1 },
	});

	return (
		<>
			{addressesForm?.fields?.length ? (
				addressesForm.fields.map((addressItem, fieldIndex) => {
					return (
						<AddressForm
							key={addressItem.id}
							currentPath={currentPath}
							locations={locations}
							fieldIndex={fieldIndex}
							addressesForm={addressesForm}
							canSetActive
						/>
					);
				})
			) : (
				<p className="w-10/12 mx-auto text-sm font-light text-center col-span-full lg:w-5/12">
					{t("form.address.empty")}
				</p>
			)}

			<Button
				disabled={
					!!form.formState.errors?.addresses && !!addressesForm.fields?.length
				}
				size="sm"
				type="button"
				variant="primary-outline"
				className="w-full mx-auto col-span-full lg:w-4/12"
				onClick={() => {
					addressesForm?.append({
						country: "INDONESIA",
						countryCode: "ID",
						state: "",
						city: "",
						postalCode: undefined,
						address: "",
						active: false,
						lat: 0,
						lng: 0,
					});
					form.trigger("addresses");
				}}
			>
				<Plus className="mr-0.5" />
				{t("form.address.add")}
			</Button>
		</>
	);
}

// * for the core therapist form
export interface FormTherapistProps {
	currentPath: string;
	mode: FormMode;
	therapist: Partial<Therapist>;
	genders: TherapistGender;
	employmentTypes: TherapistEmploymentType;
	employmentStatuses: TherapistEmploymentStatus;
	services: Service[];
	locations: Location[];
	registrationNumberSuggestions: RegistrationNumberSuggestion[];
	therapistEnums: TherapistServiceEnums[];
	/** Only on the edit form. */
	addressProps?: {
		relocationSupported: boolean;
		canManageAddresses: boolean;
		timeline?: ExternalProp<Timeline>;
		correctionLocations: Pick<Location, "id" | "city" | "state" | "country">[];
		upcomingVisits?: UpcomingVisit[];
	};
}

/**
 * The edit form's Alamat Lain: every address that is not utama on any day, minus the originals Go
 * copied into a dated row (X5). The rest is echoed read-only on save, so nothing else is written.
 */
const alamatLainOf = (addresses: TherapistAddress[] = []) => {
	const hidden = hiddenCopyIds(addresses);
	return addresses
		.filter(
			(address) =>
				!address.managedByRelocation &&
				!address.active &&
				!hidden.has(address.id),
		)
		.map(
			({
				address,
				id,
				postalCode,
				longitude,
				latitude,
				location: { country, countryCode, city, state },
			}) => ({
				active: false,
				address,
				id,
				postalCode: postalCode || undefined,
				country,
				countryCode,
				state,
				city,
				lat: latitude,
				lng: longitude,
				// filled here rather than by AddressForm, which only mounts for the tile being edited;
				// a save would zero a pin whose coordinates field was never filled
				coordinates:
					latitude != null && longitude != null
						? `${latitude}, ${longitude}`
						: "",
			}),
		);
};

export default function FormTherapist({
	currentPath,
	mode,
	therapist,
	genders,
	employmentTypes,
	employmentStatuses,
	services,
	locations,
	registrationNumberSuggestions,
	therapistEnums,
	addressProps,
}: FormTherapistProps) {
	const isDekstop = useMediaQuery("(min-width: 768px)");
	const { t } = useTranslation("therapists");
	const { props: globalProps } = usePage<
		GlobalPageProps & {
			errors: {
				fullMessages?: string;
				nik?: string[] | string;
			};
		}
	>();
	const [isLoading, setIsLoading] = useState({
		submit: false,
	});
	// id drives which sub-form renders, title is only ever displayed — keeping them
	// separate means translating a heading cannot silently stop a section rendering
	const sections = useMemo(
		() =>
			[
				{ id: "account", title: t("form.section.account") },
				{ id: "personal", title: t("form.section.personal") },
				{ id: "contract", title: t("form.section.contract") },
			] as const,
		[t],
	);

	const isUpdate = mode === "update";
	// Alamat Lain as last saved; the drafts are diffed against it (X14).
	const [savedAddresses, setSavedAddresses] = useState(() =>
		isUpdate ? alamatLainOf(therapist.addresses) : [],
	);

	const form = useForm<TherapistFormSchema>({
		// t is threaded in rather than imported by the schema module: the schema is rebuilt
		// on every render, so the messages follow the active language and the lazily-loaded
		// namespace once it lands
		resolver: zodResolver(
			// the edit form keeps the Alamat Utama outside this list, echoed on save
			getFormSchema(mode, t, { addressesRequired: !isUpdate }),
		),
		defaultValues: {
			user: {
				email: therapist?.user?.email || "",
				password: "Therapist123!",
				passwordConfirmation: "Therapist123!",
			},
			name: therapist.name || "",
			batch: therapist.batch || 1,
			registrationNumber: therapist.registrationNumber || "",
			phoneNumber: therapist.phoneNumber || "+62",
			nik: "",
			telegramId: therapist.telegramId || "",
			gender: therapist.gender || "MALE",
			religion: therapist.religion ?? null,
			employmentType: therapist.employmentType || "FLAT",
			employmentStatus: therapist.employmentStatus || "INACTIVE",
			contractStartDate: therapist.contractStartDate || "",
			contractEndDate: therapist.contractEndDate || "",
			modalities: therapist.modalities || [],
			specializations: therapist.specializations || [],
			bankDetails: therapist.bankDetails || [],
			service: therapist?.service || {},
			addresses: savedAddresses,
		},
		mode: "onBlur",
	});

	// * unsaved Alamat Lain changes (X14, X15): marked in the Rak, guarded on leaving, never stored
	const draftAddresses = useWatch({ control: form.control, name: "addresses" });
	const pendingAddresses = useMemo(
		() =>
			isUpdate
				? pendingAddressChanges(savedAddresses, draftAddresses ?? [])
				: [],
		[isUpdate, savedAddresses, draftAddresses],
	);
	const hasDrafts = pendingAddresses.length > 0;
	// "back" for the form's Batal, "visit" for a held Inertia visit, "stay" for the bar's Buang perubahan
	const [discarding, setDiscarding] = useState<
		"back" | "visit" | "stay" | null
	>(null);
	const leaving = useRef(false);
	const heldVisit = useRef<() => void>(() => {});

	useEffect(() => {
		if (!hasDrafts) return;
		const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
		window.addEventListener("beforeunload", onBeforeUnload);
		const stopGuard = router.on("before", (event) => {
			const { visit } = event.detail;
			// the form's own save, partial reloads, and a visit already confirmed go through; any
			// other visit, a logout DELETE included, waits for the admin's answer
			if (leaving.current || visit.only.length) return;
			event.preventDefault();
			heldVisit.current = () =>
				router.visit(visit.url, { method: visit.method, data: visit.data });
			setDiscarding("visit");
		});
		return () => {
			window.removeEventListener("beforeunload", onBeforeUnload);
			stopGuard();
		};
	}, [hasDrafts]);

	const discardDrafts = () => {
		const after = discarding;
		form.setValue("addresses", savedAddresses);
		setDiscarding(null);
		if (after === "back") goBackHandler();
		else if (after === "visit") {
			leaving.current = true;
			heldVisit.current();
		}
	};

	// After a Go write: the page reloads, and the Alamat Lain list starts again from what is saved.
	// Go's buttons are locked while drafts exist, so nothing unsaved is lost here.
	const reloadAfterGoWrite = () =>
		router.reload({
			only: ["timeline", "therapist", "flash"],
			onSuccess: (page) => {
				const next = alamatLainOf(
					(page.props as unknown as { therapist: Partial<Therapist> }).therapist
						.addresses,
				);
				setSavedAddresses(next);
				form.setValue("addresses", next);
			},
		});

	// Computes form alert errors based on bank details, addresses, server validation, and coordinates errors
	const formAlertErrors = useMemo(() => {
		const serverErrors = globalProps?.errors?.fullMessages;
		const formErrors = form?.formState?.errors;

		const getErrorMessage = (fieldError: any) =>
			fieldError?.root?.message || fieldError?.message;

		const bankDetailsError = getErrorMessage(formErrors?.bankDetails);
		const addressesError = getErrorMessage(formErrors?.addresses);

		// Coordinates are now optional, so no coordinate error checking needed

		// Collect all non-null, non-undefined errors
		return [bankDetailsError, addressesError, serverErrors].filter(Boolean);
	}, [form.formState.errors, globalProps?.errors?.fullMessages]);

	/**
	 * Display error messages using top alert form or toast notifications.
	 * It handles server validation errors and alerts for form-specific issues.
	 */
	useEffect(() => {
		const serverErrors = globalProps?.errors?.fullMessages;

		// Map server validation errors to specific form fields.
		//
		// These match on the English text of ActiveRecord's messages, which works only
		// because the model layer is not translated yet — the flash wrapper around them is
		// (config/locales), the messages themselves are not. Translating them will make
		// every match below silently stop firing: the toast still shows, but no field is
		// marked, so a rejected email or duplicate registration number reads as a form that
		// simply refused to save. Whoever adds `activerecord:` to the locale files has to
		// replace these with something the server states outright, e.g. keying off
		// errors.messages (which arrives alongside fullMessages) rather than its prose.
		if (serverErrors?.includes("Email")) {
			form.setError("user.email", {
				type: "server",
				message: serverErrors,
			});
		}

		if (serverErrors?.includes("Phone")) {
			form.setError("phoneNumber", {
				type: "server",
				message: serverErrors,
			});
		}

		// uniqueness is only checked server-side, so without this the duplicate lands as a
		// toast alone and the field keeps the rejected number with no marking on it
		if (serverErrors?.includes("Registration number")) {
			form.setError("registrationNumber", {
				type: "server",
				message: serverErrors,
			});
		}

		const nikErrors = globalProps?.errors?.nik;
		if (nikErrors?.length) {
			form.setError("nik", {
				type: "server",
				message: `NIK ${[nikErrors].flat().join(", ")}`,
			});
		}

		// Show the first available error in a toast notification
		if (formAlertErrors.length) {
			toast.error(formAlertErrors[0], {
				position: !isDekstop ? "top-center" : "bottom-right",
			});
		}
	}, [
		form,
		globalProps?.errors?.fullMessages,
		globalProps?.errors?.nik,
		formAlertErrors,
		isDekstop,
	]);

	const onSubmit = (values: TherapistFormSchema) => {
		const isUpdate = mode === "update";
		const submitURL = `${globalProps.adminPortal.router.adminPortal.therapistManagement.index}${isUpdate ? `/${therapist.id}` : ""}`;
		const submitConfig = {
			preserveScroll: true,
			preserveState: true,
			onStart: () => setIsLoading((prev) => ({ ...prev, submit: true })),
			onFinish: () => {
				// a refused save stays on this page, still guarded
				leaving.current = false;
				setTimeout(
					() => setIsLoading((prev) => ({ ...prev, submit: false })),
					250,
				);
			},
		};
		const { user, nik, ...restValues } = values;
		// Ensure empty date strings are sent as null
		const sanitizedValues = {
			...restValues,
			contractStartDate: restValues.contractStartDate || null,
			contractEndDate: restValues.contractEndDate || null,
			...nikPayload(nik),
		};

		// Sanitize addresses coordinates to prevent sending default values
		let finalSanitizedValues = { ...sanitizedValues };

		if (sanitizedValues.addresses) {
			const sanitizedAddresses = sanitizedValues.addresses.map((address) => {
				// Check if coordinates are actually provided (not empty)
				const hasValidCoordinates =
					address.coordinates && address.coordinates.trim() !== "";

				return {
					...address,
					// Set lat/lng to null if no coordinates provided
					lat: hasValidCoordinates && address.lat != null ? address.lat : 0,
					lng: hasValidCoordinates && address.lng != null ? address.lng : 0,
				};
			});

			// Update addresses with sanitized version
			finalSanitizedValues = {
				...sanitizedValues,
				addresses: sanitizedAddresses as typeof sanitizedValues.addresses,
			};
		}

		// Echo every row outside Alamat Lain (the chain, today's Alamat Utama, and the originals X5
		// hides) so a save never deletes one, even one that left the chain after this page loaded;
		// the server never writes them.
		if (isUpdate) {
			const savedIds = new Set(savedAddresses.map(({ id }) => id));
			finalSanitizedValues = {
				...finalSanitizedValues,
				addresses: [
					...(finalSanitizedValues.addresses ?? []),
					...(therapist.addresses ?? [])
						.filter(({ id }) => !savedIds.has(id))
						.map(({ id }) => ({ id, readOnly: true })),
				] as typeof sanitizedValues.addresses,
			};
		}

		leaving.current = true;
		const payload = deepTransformKeysToSnakeCase({
			therapist: isUpdate
				? { ...finalSanitizedValues }
				: { ...finalSanitizedValues, user },
		});

		if (isUpdate) {
			router.put(submitURL, payload, submitConfig);
		} else {
			router.post(submitURL, payload, submitConfig);
		}
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className="grid items-baseline gap-10"
			>
				{sections.map((section, sectionIndex) => {
					return (
						<FormSectionContainer
							key={section.id}
							number={sectionIndex + 1}
							title={section.title}
						>
							{section.id === "personal" && (
								<PersonalInformationForm
									currentPath={currentPath}
									genders={genders}
									locations={locations}
									nikMasked={therapist.nikMasked}
									religionSupported={therapist.religion !== undefined}
									addressSection={
										isUpdate && addressProps ? (
											<AddressSection
												addresses={therapist.addresses ?? []}
												saved={savedAddresses}
												pending={pendingAddresses}
												relocationsPath={`${globalProps.adminPortal.router.adminPortal.therapistManagement.index}/${therapist.id}/relocations`}
												relocationSupported={addressProps.relocationSupported}
												canManage={addressProps.canManageAddresses}
												timeline={addressProps.timeline}
												upcomingVisits={addressProps.upcomingVisits}
												locations={addressProps.correctionLocations}
												onGoSaved={reloadAfterGoWrite}
												onDiscardRequest={() => setDiscarding("stay")}
												renderEditor={(index) => (
													<AddressForm
														bare
														currentPath={currentPath}
														locations={locations}
														fieldIndex={index}
														canSetActive={false}
													/>
												)}
											/>
										) : undefined
									}
								/>
							)}

							{section.id === "account" && (
								<AccountInformationForm mode={mode} />
							)}

							{section.id === "contract" && (
								<ContractDetailForm
									currentPath={currentPath}
									services={services}
									employmentTypes={employmentTypes}
									employmentStatuses={employmentStatuses}
									registrationNumberSuggestions={registrationNumberSuggestions}
									therapistEnums={therapistEnums}
								/>
							)}
						</FormSectionContainer>
					);
				})}

				{/* sits directly above Save rather than at the top of the form: submitting from
				    the bottom of a long form left the alert off-screen, so a rejected save read
				    as nothing happening */}
				{!!formAlertErrors?.length && (
					<Alert variant="destructive" className="col-span-full !mb-0">
						<AlertCircle className="w-4 h-4" />
						<AlertTitle>{t("form.error_title")}</AlertTitle>
						<AlertDescription>
							<ul>
								{formAlertErrors.map((error) => (
									<li key={error}>{error}</li>
								))}
							</ul>
						</AlertDescription>
					</Alert>
				)}

				<div className="!mt-10 lg:!mt-6 gap-4 lg:gap-2 w-full flex flex-col md:flex-row lg:col-span-full md:justify-between">
					<Button
						type="button"
						variant="outline"
						className="w-full lg:w-auto"
						onClick={() =>
							hasDrafts ? setDiscarding("back") : goBackHandler()
						}
					>
						{mode === "create" ? t("form.back") : t("form.cancel")}
					</Button>

					{isUpdate && (
						<p
							className={cn(
								"text-xs md:flex-1 md:self-center md:text-right",
								hasDrafts ? "text-secondary" : "text-muted-foreground",
							)}
						>
							{hasDrafts
								? t("form.address.save_note_pending", {
										count: pendingAddresses.length,
									})
								: t("form.address.save_note")}
						</p>
					)}

					<Button
						type="submit"
						disabled={isLoading.submit}
						className={cn("w-full order-first md:order-last lg:w-auto")}
					>
						{isLoading.submit ? (
							<>
								<LoaderIcon className="animate-spin" />
								<span>
									{mode === "create" ? t("form.creating") : t("form.updating")}
								</span>
							</>
						) : (
							<span>
								{mode === "create" ? t("form.create") : t("form.save")}
							</span>
						)}
					</Button>
				</div>
			</form>

			<AlertDialog
				open={!!discarding}
				onOpenChange={(open) => !open && setDiscarding(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("form.address.discard_dialog.title")}
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="grid gap-2">
								<ul className="list-disc pl-4">
									{pendingAddresses.map(({ kind, row }) => (
										<li key={`${kind}-${row.id ?? row.address}`}>
											{[
												row.city || t("form.address.panel.new_address"),
												row.address,
											]
												.filter(Boolean)
												.join(" · ")}{" "}
											({t(`form.address.pending_what.${kind}`)})
										</li>
									))}
								</ul>
								<p>{t("form.address.discard_dialog.description")}</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("form.address.dialog_back")}
						</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={discardDrafts}
						>
							{t("form.address.discard_dialog.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Form>
	);
}
